// @flow

import Honeybadger from '@honeybadger-io/js';
import reportExtractionFailure from './reportExtractionFailure';
import type { JSONValue } from 'common/types';

const failureStack: Array<string> = [];
let usingFailureStack = false;
// Wrap root-level extraction with this higher order function to get neat error
// handling behavior
export function withFailureHandler<V>(extract: JSONValue => V): JSONValue => V {
  return json => {
    // React's dev-mode warning machinery (describeNativeComponentFrame) calls
    // function components with zero arguments to capture a native stack
    // trace for building the component-stack string of a development
    // warning. A `*Wrapper` component whose
    // body starts by extracting its own props (`extractProps(props)`)
    // forwards that "no argument" state on as a single, explicit `undefined`
    // call — indistinguishable here from a genuine one by argument count
    // alone. But a genuine call can never look like this: React's own
    // `createElement` normalizes a nullish props argument to `{}` before a
    // component ever sees it, so `json === undefined` reaching this point is
    // only possible via that probe. Extraction still runs and degrades
    // gracefully either way; only the side effect below (the alert /
    // Honeybadger report meant for a real extraction failure) is skipped for
    // a call that never really happened.
    const isReactComponentFrameProbe = json === undefined;
    // Allow `failure` extractor to customize its logic based on whether the
    // extraction has been wrapped with this function or not.
    usingFailureStack = true;
    try {
      const returnValue = extract(json);
      // Now `failureStack` should be populated if any errors happened when extracting.
      //
      // Do not handle this on the server side, though, as we get better error
      // messages on browser side and debugging is also easier in the browser.
      // Failures will be handled again when React mounts, and debugging extractor
      // failures on browser-side is waaay easier than debugging an error
      // raised when rendering a component on the server side. See
      // reportExtractionFailure.server.js for the server-side no-op.
      if (failureStack.length > 0 && !isReactComponentFrameProbe) {
        // Make a deep clone of the failure stack to ensure that the
        // failureStack doesn't get modified from under us
        const snapshotStack = JSON.parse(JSON.stringify(failureStack));
        reportExtractionFailure(snapshotStack, json);
      }
      return returnValue;
    } catch (e) {
      // Any errors that get here are result of real runtime errors, and we should
      // not handle them.
      throw e;
    } finally {
      // Reset the state regardless if there was an error or not
      usingFailureStack = false;
      failureStack.length = 0;
    }
  };
}

export function failure<V>(errorMessage: string, fallback: () => V): V {
  if (!usingFailureStack) {
    // This situation means that the extraction pipeline for this particular case
    // has not been wrapped with the failure handler function.
    //
    // In that case, handle the failures as before.
    const error = new Error(errorMessage);
    if (__DEV__) {
      throw error;
    } else {
      Honeybadger.notify(error);
      return fallback();
    }
  }
  // This extraction pipeline has been wrapped with a failure handling pipeline.
  // The failure handler will handle all errors received during extraction.
  failureStack.push(errorMessage);
  return fallback();
}
